import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Typography,
  Upload,
  Flex,
  Grid,
  App,
  Checkbox,
  message,
  Modal,
} from "antd";
import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import JsBarcode from "jsbarcode";

import {
  createOrUpdateItem,
  fetchItemCategories,
  fetchItemGroups,
  fetchItemModels,
  fetchItemOrigins,
  fetchItemRacks,
  fetchItemUnits,
  fetchItemsList,
  fetchBranchList,
  fetchItemCode,
  fetchDistributors,
  fetchManufactures,
  createDistributors,
  createManufactures,
  fetchGenericNames,
} from "../itemManageThunks";
import { fetchSingleInstitutionDetails } from "../../../store/commonThunks/lookups";
import { setIsEditItem, setSelectedItem } from "../itemManageSlice";
import "./style.css";
import { Select as AntSelect } from "antd";
import { getAccessType, objectToFormData, Trial_Limits } from "../../../lib/functions";
import InputField from "../../../components/Input/Input";
import InputNumberField from "../../../components/Input/InputNumber";
import SelectField from "../../../components/Input/Select";
import TextAreaField from "../../../components/Input/TextAreaField";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import AddMasterModal from "./AddMasterModal";
import { createOrUpdateGroup } from "../../ItemGroup/groupThunks";
import { saveCategory } from "../../category/categoryThunks";
import { createOrUpdateUnit } from "../../Units/unitsThunks";
import { createOrUpdateModel } from "../../Models/modelsThunks";
import { createOrUpdateOrigin } from "../../Origins/originsThunks";
import { createOrUpdateRack } from "../../Racks/racksThunks";
import { selectAuthInfo } from "../../Auth/authSelectors";
import Swal from "sweetalert2";
import { saveGenericName } from "../../GenericNames/genericNameThunks";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const yesNo = [
  {
    label: "Yes",
    value: "yes",
  },
  {
    label: "No",
    value: "no",
  },
];

const CreateItemForm = () => {
  const authInfo = useSelector(selectAuthInfo);
  const lookups = useSelector((state) => state.lookups);
  const crossBranchSyncEnabled =
    lookups?.institutionData?.enable_cross_branch_item_sync === "yes";

  const { modal } = App.useApp();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const {
    itemCode,
    isEditItem,
    selectedItem,
    categories,
    groups,
    models,
    origins,
    racks,
    units,
    branches,
    distributors,
    genericNames,
    items = [],
    manufactures,
  } = useSelector((state) => state.itemManage);

  // Dedupe (by name, case-insensitive) and sort A→Z; backend can return
  // repeat rows when default generic names get re-seeded.
  const sortedGenericNames = useMemo(() => {
    const seen = new Set();
    return (genericNames ?? [])
      .filter((g) => {
        const key = g.generic_name?.trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) =>
        a.generic_name.trim().localeCompare(b.generic_name.trim(), undefined, {
          sensitivity: "base",
        })
      );
  }, [genericNames]);

  const isProductTrialLimitReached =
    authInfo?.trial_mode == "yes" &&
    !isEditItem &&
    items.length >= Trial_Limits.product_count;

  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  let [access_type, access_type_set] = useState("");

  const params = new URLSearchParams(location.search);
  const returnTo = params.get("returnUrl");
  const [saving, setSaving] = useState(false);
  const [manufactureName, setManufactureName] = useState("");
  const [manufactureGcpLength, setManufactureGcpLength] = useState(null);
  const [distributorName, setDistributorName] = useState("");
  const [openManufactureModal, setOpenManufactureModal] = useState(false);
  const [openDistributorModal, setOpenDistributorModal] = useState(false);
  const [openMasterModal, setOpenMasterModal] = useState(false);
  const [masterType, setMasterType] = useState(null);
  const [pendingSelect, setPendingSelect] = useState(null);

  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedModelId, setSelectedModelId] = useState(null);

  // Per-branch opening qty/rate, only used when 2+ branches are selected at creation.
  const [branchOpeningData, setBranchOpeningData] = useState({});
  // UI-only helper: enter a GST-inclusive price and derive the ex-GST Retail Rate.
  // Kept out of form values so it is never sent to the backend.
  const [priceInclGst, setPriceInclGst] = useState(false);
  const [inclusivePrice, setInclusivePrice] = useState(null);
  const watchedTaxPer = Form.useWatch("tax_per", form);
  const taxLabel = authInfo?.country?.toLowerCase() === "india" ? "GST" : "VAT";
  const taxMissing =
    watchedTaxPer === undefined || watchedTaxPer === null || watchedTaxPer === "";
  const watchedBranchList = Form.useWatch("branch_list", form) || [];
  const watchedManufactureName = Form.useWatch("manufacture_name", form);
  const watchedDistributerName = Form.useWatch("distributer_name", form);
  // Reactive (unlike form.getFieldValue in render): service items carry no stock.
  const isServiceItem = Form.useWatch("is_service", form) === true;
  // Same for asset items -- a Purchase Bill "Asset" line is also non-stock.
  const isAssetItem = Form.useWatch("is_asset", form) === true;
  const isNonStockItem = isServiceItem || isAssetItem;

  // New service/asset item: clear anything typed before the checkbox was ticked
  // so 0 is what gets saved. Edit keeps the stored values and only disables the fields.
  useEffect(() => {
    if (!isNonStockItem || isEditItem) return;
    form.setFieldsValue({ opening_qty: 0, opening_rate: 0 });
    setBranchOpeningData((prev) =>
      Object.fromEntries(
        Object.keys(prev).map((branchId) => [
          branchId,
          { opening_qty: 0, opening_rate: 0 },
        ])
      )
    );
  }, [isNonStockItem, isEditItem, form]);

  const isIndia =
    authInfo?.country?.toLowerCase() === "india";

  const gstEnabled = authInfo?.gst_enabled === "yes";
  // India: hide/disable GST when disabled. Non-India: VAT always applies.
  const showTax = !isIndia || gstEnabled;
  const isGSTDisabled =
    isIndia && authInfo?.gst_enabled === "no";
  const isGSTEnabled =
    isIndia && authInfo?.gst_enabled === "yes";
  // const isPharmacy =
  //   (authInfo?.company_type === "Drug Store" ||
  //     authInfo?.company_type === "Pharmacy") &&
  //   !isIndia;
  const isUAE = authInfo?.country === "United Arab Emirates";

  const isDrugStore = authInfo?.company_type === "Drug Store" && isUAE;
  const isPharmacy = authInfo?.company_type === "Pharmacy";
  console.log('authInfo', authInfo);
  useEffect(() => {
    const access = getAccessType("item_entry");
    access_type_set(access);
  }, []);
  useEffect(() => {
    if (
      !isEditItem &&
      authInfo?.userInfo.branch_id &&
      branches?.length
    ) {
      if (crossBranchSyncEnabled) {
        // Opt-in: no manual branch selection — every item spans every branch.
        const allBranchIds = branches.map((b) => b.id);
        form.setFieldValue("branch_list", allBranchIds);
        setBranchOpeningData((prev) => {
          const next = {};
          allBranchIds.forEach((branchId) => {
            next[branchId] = prev[branchId] || { opening_qty: 0, opening_rate: 0 };
          });
          return next;
        });
      } else {
        form.setFieldValue("branch_list", [authInfo.userInfo.branch_id]);
      }
    }
  }, [authInfo, branches, form, isEditItem, crossBranchSyncEnabled]);
  // Retail Rate = inclusive price / (1 + tax%). 4 decimals kept so that
  // rate * (1 + tax%) gets back to the typed inclusive price.
  useEffect(() => {
    if (!priceInclGst) return;
    const tax = Number(watchedTaxPer);
    if (
      inclusivePrice === null ||
      inclusivePrice === undefined ||
      watchedTaxPer === undefined ||
      watchedTaxPer === null ||
      watchedTaxPer === "" ||
      Number.isNaN(tax)
    ) {
      return;
    }
    const exclusive = Number(inclusivePrice) / (1 + tax / 100);
    form.setFieldValue("sale_rate", Math.round(exclusive * 10000) / 10000);
  }, [priceInclGst, inclusivePrice, watchedTaxPer, form]);

  useEffect(() => {
    if (isGSTDisabled) {
      form.setFieldValue("tax_per", 0);
    }
  }, [isGSTDisabled, form]);

  useEffect(() => {
    dispatch(fetchSingleInstitutionDetails());
    dispatch(fetchBranchList());
    dispatch(fetchItemGroups());
    dispatch(fetchItemCategories());
    dispatch(fetchItemModels());
    dispatch(fetchItemOrigins());
    dispatch(fetchItemRacks());
    dispatch(fetchItemUnits());
    dispatch(fetchDistributors());
    dispatch(fetchManufactures());
    dispatch(fetchGenericNames());
  }, [dispatch]);

  useEffect(() => {
    if (isEditItem && selectedItem) {
      const normalized = {
        ...selectedItem,
        is_service: selectedItem.is_service === "yes",
        is_asset: selectedItem.is_asset === "yes",
        is_serial: selectedItem.is_serial === "yes",
      };

      form.setFieldsValue(normalized);
      setPreview(selectedItem.photo || selectedItem.photo_url || null);

      // 🔑 pre-populate the cascade so dropdowns aren't disabled/empty on load
      setSelectedGroupId(selectedItem.group_id || null);
      setSelectedCategoryId(selectedItem.category_id || null);
      setSelectedModelId(selectedItem.model_id || null);
      setPriceInclGst(false);
      setInclusivePrice(null);
    } else {
      form.resetFields();
      setPreview(null);
      setPhoto(null);
      setSelectedGroupId(null);
      setSelectedCategoryId(null);
      setSelectedModelId(null);
      setPriceInclGst(false);
      setInclusivePrice(null);
    }
  }, [isEditItem, selectedItem, form]);
  const filteredCategories = useMemo(
    () => (categories || []).filter((c) => c.group_id === selectedGroupId),
    [categories, selectedGroupId]
  );

  const filteredModels = useMemo(
    () => (models || []).filter((m) => m.category_id === selectedCategoryId),
    [models, selectedCategoryId]
  );

  const localeSort = (list, key) =>
    [...list].sort((a, b) =>
      (a[key] ?? "").localeCompare(b[key] ?? "", undefined, { sensitivity: "base" })
    );

  const sortedGroups = useMemo(
    () => localeSort(groups ?? [], "group_name"),
    [groups]
  );

  const sortedFilteredCategories = useMemo(
    () => localeSort(filteredCategories, "category_name"),
    [filteredCategories]
  );

  const sortedFilteredModels = useMemo(
    () => localeSort(filteredModels, "model_name"),
    [filteredModels]
  );

  useEffect(() => {
    if (!isEditItem) {
      dispatch(fetchItemCode());
    }
  }, [dispatch, isEditItem]);

  useEffect(() => {
    if (!isEditItem) {
      form.setFieldValue("item_code", itemCode);
    }
  }, [itemCode, isEditItem]);

  useEffect(() => {
    if (!pendingSelect) return;

    const { type, name } = pendingSelect;

    switch (type) {
      case "group": {
        const found = groups?.find(
          (g) => g.group_name?.toLowerCase() === name
        );
        if (found) {
          form.setFieldValue("group_id", found.group_id);
          setSelectedGroupId(found.group_id); // 👈 add
          setPendingSelect(null);
        }
        break;
      }

      case "category": {
        const found = categories?.find(
          (c) =>
            c.category_name?.toLowerCase() === name &&
            c.group_id === selectedGroupId
        );
        if (found) {
          form.setFieldValue("category_id", found.category_id);
          setSelectedCategoryId(found.category_id); // 👈 add
          setPendingSelect(null);
        }
        break;
      }

      case "model": {
        const found = models?.find(
          (m) =>
            m.model_name?.toLowerCase() === name &&
            m.category_id === selectedCategoryId
        );
        if (found) {
          form.setFieldValue("model_id", found.model_id);
          setSelectedModelId(found.model_id); // 👈 add
          setPendingSelect(null);
        }
        break;
      }
      case "unit": {
        const found = units?.find(
          (u) => u.unit_name?.toLowerCase() === name
        );
        if (found) {
          form.setFieldValue("unit_id", found.unit_id);
          setPendingSelect(null);
        }
        break;
      }



      case "generic": {
        const found = genericNames?.find(
          (g) => g.generic_name?.toLowerCase() === name
        );
        if (found) {
          form.setFieldValue("generic_name_id", found.generic_name_id);
          setPendingSelect(null);
        }
        break;
      }

      case "origin": {
        const found = origins?.find(
          (o) => o.origin_name?.toLowerCase() === name
        );
        if (found) {
          form.setFieldValue("origin_id", found.origin_id);
          setPendingSelect(null);
        }
        break;
      }

      case "rack": {
        const found = racks?.find(
          (r) => r.name?.toLowerCase() === name
        );
        if (found) {
          form.setFieldValue("rack_id", found.id);
          setPendingSelect(null);
        }
        break;
      }

      default:
        break;
    }
  }, [
    pendingSelect,
    groups,
    categories,
    units,
    models,
    origins,
    racks,
    genericNames,
    form,
    selectedGroupId,
    selectedCategoryId,
    selectedModelId,
  ]);
  const barcodeCanvasRef = useRef(null);
  const [generatedBarcode, setGeneratedBarcode] = useState("");
  const [showBarcodePreview, setShowBarcodePreview] = useState(false);
  const handleGroupSelect = (value) => {
    if (value === "add_new") {
      setMasterType("group");
      setOpenMasterModal(true);
      return;
    }
    setSelectedGroupId(value);
    setSelectedCategoryId(null);
    setSelectedModelId(null);
    form.setFieldsValue({
      category_id: undefined,
      model_id: undefined,
    });
  };

  const handleCategorySelect = (value) => {
    if (value === "add_new") {
      if (!selectedGroupId) {
        return modal.warning({ title: "Please select a Group first" });
      }
      setMasterType("category");
      setOpenMasterModal(true);
      return;
    }
    setSelectedCategoryId(value);
    setSelectedModelId(null);
    form.setFieldsValue({ model_id: undefined });
  };

  const handleModelSelect = (value) => {
    if (value === "add_new") {
      if (!selectedCategoryId) {
        return modal.warning({ title: "Please select a Category first" });
      }
      setMasterType("model");
      setOpenMasterModal(true);
      return;
    }
    setSelectedModelId(value);
  };

  const handleGenericSelect = (value) => {
    if (value === "add_new") {
      form.setFieldValue("generic_name_id", undefined);
      setMasterType("generic");
      setOpenMasterModal(true);
    }
  };
  const handleGenerateBarcode = () => {
    // Generate a 12-digit numeric barcode (EAN-13 compatible)
    const code = String(Date.now()).slice(-12).padStart(12, "0");

    setGeneratedBarcode(code);
    setShowBarcodePreview(true);

    // Fill the form field
    form.setFieldValue("item_barcode", code);

    // Render barcode on next tick (after state update)
    setTimeout(() => {
      if (barcodeCanvasRef.current) {
        JsBarcode(barcodeCanvasRef.current, code, {
          format: "CODE128",
          lineColor: "#000",
          width: 2,
          height: 60,
          displayValue: true,
        });
      }
    }, 50);
  };

  const handleDownloadBarcode = () => {
    const canvas = barcodeCanvasRef.current;
    if (!canvas) return;

    const link = document.createElement("a");
    link.download = `barcode-${generatedBarcode}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const handleCancel = () => {
    dispatch(setIsEditItem(false));
    dispatch(setSelectedItem(null));
    navigate("/inventory/product?tab=items");
  };

  const handleManufactureSelect = (value, option) => {
    if (value === "add_new") {
      setOpenManufactureModal(true); // 👈 open modal
      return;
    }
    if (!value) {
      form.setFieldsValue({ manufacture_name: "", manufacture_id: 0 });
      return;
    }
    form.setFieldsValue({
      manufacture_name: value?.display_text,
      manufacture_id: value?.raw?.id,
    });
  };

  const handleSaveManufacture = () => {

    if (!manufactureName || !manufactureName.trim()) {
      return modal.warning({ title: "Manufacture name cannot be empty" });
    }

    dispatch(
      createManufactures({
        action: "create",
        name: manufactureName,
        company_prefix_length: manufactureGcpLength || null,
      })
    )
      .unwrap()
      .then((res) => {
        modal.success({ title: res.msg });
        // Refresh the list
        dispatch(fetchManufactures());
        setManufactureName("");
        setManufactureGcpLength(null);
        setOpenManufactureModal(false);
      })
      .catch((err) => modal.error({ title: err.message }));
  };

  const handleDistributorSelect = (value, option) => {
    if (value === "add_new") {
      setOpenDistributorModal(true); // 👈 open modal
      return;
    }
    if (!value) {
      form.setFieldsValue({ distributer_name: "", distributer_id: 0 });
      return;
    }

    form.setFieldsValue({
      distributer_name: value?.display_text,
      distributer_id: value?.raw?.id,
    });
  };
  const handleBranchChange = (values) => {
    let nextBranchList;
    if (values.includes("all")) {
      // Select all branch IDs
      nextBranchList = branches.map((b) => b.id);
      form.setFieldValue("branch_list", nextBranchList);
    } else {
      nextBranchList = values;
      form.setFieldValue("branch_list", values);
    }

    // Keep the per-branch opening data map in sync: keep existing entries
    // for branches still selected, default new ones to 0, drop the rest.
    setBranchOpeningData((prev) => {
      const next = {};
      nextBranchList.forEach((branchId) => {
        next[branchId] = prev[branchId] || { opening_qty: 0, opening_rate: 0 };
      });
      return next;
    });
  };

  const handleSaveDistributor = () => {

    if (!distributorName || !distributorName.trim()) {
      return modal.warning({ title: "Distributor name cannot be empty" });
    }

    dispatch(createDistributors({ action: "create", name: distributorName }))
      .unwrap()
      .then((res) => {
        modal.success({ title: res.msg });
        // Refresh the list
        dispatch(fetchDistributors());
        setDistributorName("");
        setOpenDistributorModal(false);
      })
      .catch((err) => modal.error({ title: err.message }));
  };

  const handleSave = async (values, confirmSync = false) => {

    const trialMode = authInfo?.trial_mode;
    const userName = authInfo?.userInfo?.name || "User";
    if (!isEditItem && trialMode == "yes" && items.length >= Trial_Limits.product_count && !isEditItem) {
      return modal.info({
        title: "Free Limit Reached",
        content:
          `Hi ${userName}, you are currently in Free mode. You can create up to 10 products during the Free Version.`,
      });
    }
    const isService = values.is_service === true;
    const isAsset = values.is_asset === true;
    const isNonStock = isService || isAsset;

    const isDrugStore =
      authInfo?.company_type === "Drug Store" ||
      authInfo?.company_type === "Pharmacy";
    const actionType = isEditItem ? "update" : "create";

    // Manual Validations (you can also move some to Form.Item rules)
    if (!values.item_name?.trim()) {
      return modal.warning({ title: "Product Name is Required" });
    }
    if (values.item_name.length > 100) {
      return modal.warning({ title: "Product Name max size exceeded" });
    }
    if (!values.unit_id) {
      return modal.warning({ title: "Select a Unit" });
    }
    if (values.narration?.length > 100) {
      return modal.warning({ title: "Description max size exceeded" });
    }
    // if (!isService && (!values.purchase_rate || values.purchase_rate == 0)) { //purchase rate is made non mandatory now as per customer feedback
    //   return modal.warning({ title: "Purchase Rate is Required" });
    // }
    if (!isNonStock && (!values.sale_rate || values.sale_rate == 0)) {
      return modal.warning({ title: "Retail Rate is Required" });
    }
    if (!isNonStock && isDrugStore && (!values.gtn_num || values.gtn_num == 0)) {
      return modal.warning({ title: "GTN Number is Required" });
    }
    // if (!isService && (!values.opening_rate || values.opening_rate == 0)) {
    //   return modal.warning({ title: "Opening Rate is Required" });
    // }
    // if (!values.opening_qty || values.opening_qty == 0) {
    //   return modal.warning({ title: "Opening Quantity is Required" });
    // }
    const usePerBranchOpening =
      actionType === "create" && (values.branch_list?.length || 0) >= 2;

    if (!usePerBranchOpening) {
      if (
        !isNonStock &&
        (values.opening_rate === undefined ||
          values.opening_rate === null ||
          values.opening_rate === "")
      ) {
        return modal.warning({ title: "Opening Rate is Required" });
      }

      if (
        !isNonStock &&
        (values.opening_qty === undefined ||
          values.opening_qty === null ||
          values.opening_qty === "")
      ) {
        return modal.warning({ title: "Opening Quantity is Required" });
      }
    } else if (!isNonStock) {
      const missingBranch = values.branch_list.find((branchId) => {
        const entry = branchOpeningData[branchId];
        return (
          !entry ||
          entry.opening_qty === undefined ||
          entry.opening_qty === null ||
          entry.opening_qty === "" ||
          entry.opening_rate === undefined ||
          entry.opening_rate === null ||
          entry.opening_rate === ""
        );
      });
      if (missingBranch) {
        const branchName =
          branches?.find((b) => b.id === missingBranch)?.name || missingBranch;
        return modal.warning({
          title: `Opening Stock Qty/Rate is Required for branch "${branchName}"`,
        });
      }
    }

    if (
      !isNonStock &&
      showTax &&
      !isGSTDisabled &&
      (values.tax_per === undefined ||
        values.tax_per === null ||
        values.tax_per === "")
    ) {
      return modal.warning({ title: "Product VAT% is Required" });
    }

    if (actionType === "create" && values.branch_list?.length === 0) {
      return modal.warning({ title: "Please Provide Branch Details" });
    }

    // Prepare Payload
    const payload = {
      ...values,
      is_service: values.is_service ? "yes" : "no",
      is_asset: values.is_asset ? "yes" : "no",
      is_serial: values.is_serial ? "yes" : "no",
      item_id: selectedItem?.item_id || 0,
      item_code: values.item_code?.trim(),
      item_name: values.item_name?.trim(),
      item_barcode: values.item_barcode?.trim(),
      group_id: values.group_id || 0,
      category_id: values.category_id || 0,
      model_id: values.model_id || 0,
      origin_id: values.origin_id || 0,
      rack_id: values.rack_id || 0,
      tax_acc_id: values.selectedTaxAcc?.acc_id || 0,
      distributer_name: values.distributer_name || "",
      manufacture_name: values.manufacture_name || "",
      distributer_id: values.distributer_id || 0,
      manufacture_id: values.manufacture_id || 0,
      gtn_num: values.gtn_num || 0,
      unit_id: values.unit_id || null,
      return_days: values.return_days || 0,
      action: actionType,
      branch_list: values.branch_list,
      photo: values.photo,
      prev_photo: values.prev_photo,
      group_name: actionType === "create" ? values.group_name : undefined,
      category_name: actionType === "create" ? values.category_name : undefined,
      origin_name: actionType === "create" ? values.origin_name : undefined,
      unit_name: actionType === "create" ? values.unit_name : undefined,
      model_name: actionType === "create" ? values.model_name : undefined,
      generic_name_id:
        values.generic_name_id || 0,
      moh_invoice_rate: isDrugStore ? (values.moh_invoice_rate || 0) : undefined,
      branch_opening_data: usePerBranchOpening
        ? JSON.stringify(branchOpeningData)
        : undefined,
    };

    // Building FormData manually
    const formData = new FormData();

    // append all keys directly
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // backend expects comma-separated string
          formData.append(key, value.join(","));
        } else {
          formData.append(key, value);
        }
      }
    });

    // ✅ attach photo if available
    if (photo instanceof File) {
      formData.append("photo", photo); // 'photo' key must match uploadSingleItem field name
    }

    if (confirmSync) {
      formData.append("confirmSync", "true");
    }

    try {
      const result = await dispatch(createOrUpdateItem(formData)).unwrap();

      if (result?.needsSyncConfirmation) {
        const siblingCount = result.siblingCount ?? (result.affectedBranches || []).length;
        modal.confirm({
          title: "Update linked branches?",
          content: `This will update this item in ${siblingCount} other branch${siblingCount === 1 ? "" : "es"}. Continue?`,
          okText: "Update",
          cancelText: "Cancel",
          onOk: () => handleSave(values, true),
        });
        return;
      }

      modal.success({
        title: isEditItem
          ? "Item updated successfully"
          : "Item created successfully",
      });

      dispatch(fetchItemsList());
      dispatch(setSelectedItem(null));
      dispatch(setIsEditItem(false));
      // 🔥 Return to page coming from if returnTo exists
      navigate(
        returnTo && returnTo.trim()
          ? `${returnTo}?returnUrl=${returnTo}`
          : "/item-manage",
      );
    } catch (error) {

      modal.error({
        title: error?.data?.message || error?.error || error || "Save failed",
      });
    }
  };

  const handleSaveMaster = async (values) => {
    let action;

    switch (masterType) {
      case "group":
        action = dispatch(
          createOrUpdateGroup({
            action: "create",
            group_id: 0,
            group_name: values.name,
          })
        );
        break;

      case "category":
        action = dispatch(
          saveCategory({
            action: "create",
            category_id: 0,
            category_name: values.name,
            group_id: values.group_id, // 👈 from modal, not outer state
          })
        );
        break;

      case "model":
        action = dispatch(
          createOrUpdateModel({
            action: "create",
            model_id: 0,
            model_name: values.name,
            category_id: values.category_id, // 👈 from modal
          })
        );
        break;

      case "generic":
        action = dispatch(
          saveGenericName({
            action: "create",
            generic_name_id: 0,
            generic_name: values.name,
          })
        );
        break;

      case "unit":
        action = dispatch(
          createOrUpdateUnit({
            action: "create",
            unit_id: 0,
            unit_name: values.unit_name,
            unit_symbol: values.unit_symbol, // ✅ NEW
          })
        );
        break;



      case "origin":
        action = dispatch(
          createOrUpdateOrigin({
            action: "create",
            origin_id: 0,
            origin_name: values.name,
          })
        );
        break;

      case "rack":
        action = dispatch(
          createOrUpdateRack({
            action: "create",
            id: 0,
            name: values.name,
          })
        );
        break;

      default:
        return;
    }

    await action.unwrap();

    // keep the main form's cascade state aligned with what was just created under
    if (masterType === "category" && values.group_id) {
      setSelectedGroupId(values.group_id);
    } else if (masterType === "model") {
      if (values.group_id) setSelectedGroupId(values.group_id);
      if (values.category_id) setSelectedCategoryId(values.category_id);
    }

    setPendingSelect({
      type: masterType,
      name:
        masterType === "unit"
          ? values.unit_name.trim().toLowerCase()
          : values.name.trim().toLowerCase(),
    });

    dispatch(fetchItemGroups());
    dispatch(fetchItemCategories());
    dispatch(fetchItemUnits());
    dispatch(fetchItemModels());
    dispatch(fetchItemOrigins());
    dispatch(fetchItemRacks());
    dispatch(fetchGenericNames());
    setOpenMasterModal(false);
  };

  const onPhotoChange = (info) => {
    const fileObj = info.file?.originFileObj || info.file; // fallback for safety
    if (!fileObj) return;

    setPhoto(fileObj);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
    };
    reader.readAsDataURL(fileObj);
  };

  return (
    <div className="invoice-form item-manage-form">
      <Title level={2} style={{ marginBottom: "32px" }}>
        {isEditItem ? "Update Item" : "Create New Item"}
      </Title>

      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) =>
          required ? (
            <>
              {label} <span style={{ color: "#ff4d4f" }}>*</span>
            </>
          ) : (
            label
          )
        }
        onFinish={async () => {
          try {
            const values = await form.validateFields();
            await handleSave(values);
          } catch (err) {
            // form validation errors will stop here automatically
          }
        }}
        autoComplete="off"
        variant="filled"
        style={{ maxWidth: "1400px" }}
        className="responsive-form"
        colon={false}
        labelCol={{
          span: 8,
        }}
        wrapperCol={{
          span: 18,
        }}
      >
        {!screens.xs && (
          <Form.Item
            style={{
              marginTop: "24px",
            }}
          >
            <Flex style={{ marginBottom: "32px", width: "25%" }} gap={32}>
              <Button
                size="large"
                shape="default"
                onClick={handleCancel}
                className="traic-button"
              >
                Back
              </Button>
              {access_type == "WRITE" && (
                <>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    className="traic-button"
                  >
                    {isEditItem ? "Update" : "Save"}
                  </Button>
                </>
              )}
            </Flex>
          </Form.Item>
        )}

        <Form.Item
          // label="Service / Serial"
          label="Service"
          required={false}
          colon={false}
          style={{
            width: !screens.md || !screens.xl ? "100%" : "61%",
            paddingLeft: screens.xl ? "5rem" : "0px",
            marginBottom: "32px",
          }}
        >
          <Flex gap={48} align="center">
            {/* Service Item */}
            <Form.Item name="is_service" valuePropName="checked" noStyle>
              <Checkbox disabled={isAssetItem}>Service Item</Checkbox>
            </Form.Item>

            {/* Asset Item -- non-stock, purchasable via Purchase Bill only, posts
                to a user-chosen Fixed Asset account instead of the default
                Purchase account. Mutually exclusive with Service Item. */}
            <Form.Item name="is_asset" valuePropName="checked" noStyle>
              <Checkbox disabled={isServiceItem}>Asset Item</Checkbox>
            </Form.Item>

            {/* Serial Item */}
            {/* <Form.Item name="is_serial" valuePropName="checked" noStyle>
              <Checkbox>Serial Item</Checkbox>
            </Form.Item> */}
          </Flex>
        </Form.Item>

        {/* <Flex gap={16} wrap > */}
        <Flex
          gap={!screens.md || !screens.xl ? 10 : 80}
          justify="center"
          style={{
            flexDirection: !screens.md || !screens.xl ? "column" : "row",
            width: "100%",
            marginBottom: "24px",
          }}
        >
          <Flex
            vertical
            gap={10}
            style={{
              width: !screens.md || !screens.xl ? "100%" : "40%",
            }}
          >
            <Form.Item label="Item Code" name="item_code">
              <InputField />
            </Form.Item>

            <Form.Item
              label="Product Name"
              name="item_name"
              rules={[{ required: true, message: "Product Name is Required" }]}
            >
              <InputField placeholder="Enter item name" />
            </Form.Item>

            {(isDrugStore || isPharmacy) && (
              <Form.Item
                label="Product Generic Name"
                name="generic_name_id"
              >
                <SelectField
                  placeholder="Select generic name"
                  data-testid="generic-name-select"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onSelect={(value) => {

                    if (value === "add_new") {

                      form.setFieldValue(
                        "generic_name_id",
                        undefined
                      );

                      setMasterType("generic");

                      setOpenMasterModal(true);
                    }
                  }}
                >

                  <Option value="add_new">
                    + Add New
                  </Option>

                  {sortedGenericNames.map((g) => (

                    <Option
                      key={g.generic_name_id}
                      value={g.generic_name_id}
                    >
                      {g.generic_name}
                    </Option>

                  ))}

                </SelectField>
              </Form.Item>
            )}
            <Form.Item label="Barcode" name="item_barcode">
              <InputField placeholder="Enter barcode" />
            </Form.Item>

            {/* Generate button — show only when barcode is empty */}
            <Form.Item label=" " colon={false}>
              <Button
                type="dashed"
                onClick={handleGenerateBarcode}
                disabled={!!form.getFieldValue("item_barcode")}
              >
                Generate Barcode
              </Button>
            </Form.Item>

            {/* Barcode preview + download */}
            {showBarcodePreview && (
              <Form.Item label=" " colon={false}>
                <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
                  <canvas ref={barcodeCanvasRef} />
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={handleDownloadBarcode}
                  >
                    Download Barcode
                  </Button>
                </div>
              </Form.Item>
            )}
            <Form.Item label="Group" name="group_id">
              <SelectField
                placeholder="Select group"
                data-testid="group-select"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
                onSelect={handleGroupSelect}
              >
                <Option value="add_new">+ Add New</Option>
                {sortedGroups.map((g) => (
                  <Option key={g.group_id} value={g.group_id}>
                    {g.group_name}
                  </Option>
                ))}
              </SelectField>
            </Form.Item>

            <Form.Item label="Category" name="category_id">
              <SelectField
                placeholder={selectedGroupId ? "Select category" : "Select a group first"}
                data-testid="category-select"
                disabled={!selectedGroupId}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
                onSelect={handleCategorySelect}
              >
                <Option value="add_new">+ Add New</Option>
                {sortedFilteredCategories.map((c) => (
                  <Option key={c.category_id} value={c.category_id}>
                    {c.category_name}
                  </Option>
                ))}
              </SelectField>
            </Form.Item>

            <Form.Item label={(isDrugStore || isPharmacy) ? "Sub Category" : "Model"} name="model_id">
              <SelectField
                placeholder={selectedCategoryId ? "Select" : "Select a category first"}
                data-testid="model-select"
                disabled={!selectedCategoryId}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                }
                onSelect={handleModelSelect}
              >
                <Option value="add_new">+ Add New</Option>
                {sortedFilteredModels.map((m) => (
                  <Option key={m.model_id} value={m.model_id}>
                    {m.model_name}
                  </Option>
                ))}
              </SelectField>
            </Form.Item>
            {(isDrugStore || isPharmacy) && (
              <Form.Item label="Product Generic Name" name="generic_name_id">
                <SelectField
                  placeholder="Select generic name"
                  data-testid="generic-name-select"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
                  }
                  onSelect={handleGenericSelect}
                >
                  <Option value="add_new">+ Add New</Option>
                  {sortedGenericNames.map((g) => (
                    <Option key={g.generic_name_id} value={g.generic_name_id}>
                      {g.generic_name}
                    </Option>
                  ))}
                </SelectField>
              </Form.Item>
            )}


            <Form.Item label="Rack" name="rack_id">
              <SelectField
                placeholder="Select rack"
                data-testid="rack-select"
                onSelect={(value) => {
                  if (value === "add_new") {
                    form.setFieldValue("rack_id", undefined);
                    setMasterType("rack");
                    setOpenMasterModal(true);
                  }
                }}
              >
                <Option value="add_new">+ Add New</Option>
                {racks?.map((r) => (
                  <Option key={r.id} value={r.id}>
                    {r.name}
                  </Option>
                ))}
              </SelectField>
            </Form.Item>
            <Form.Item label="Origin" name="origin_id">
              <SelectField
                placeholder="Select origin"
                data-testid="origin-select"
                onSelect={(value) => {
                  if (value === "add_new") {
                    form.setFieldValue("origin_id", undefined);
                    setMasterType("origin");
                    setOpenMasterModal(true);
                  }
                }}
              >
                <Option value="add_new">+ Add New</Option>
                {origins?.map((o) => (
                  <Option key={o.origin_id} value={o.origin_id}>
                    {o.origin_name}
                  </Option>
                ))}
              </SelectField>
            </Form.Item>

            <Form.Item
              label="Unit"
              name="unit_id"
              rules={[{ required: true, message: "Select Unit" }]}
            >
              <SelectField
                placeholder="Select unit"
                data-testid="unit-select"
                onSelect={(value) => {
                  if (value === "add_new") {
                    form.setFieldValue("unit_id", undefined);
                    setMasterType("unit");
                    setOpenMasterModal(true);
                  }
                }}
              >
                <Option value="add_new">+ Add New</Option>
                {units?.map((u) => (
                  <Option key={u.unit_id} value={u.unit_id}>
                    {u.unit_name}
                  </Option>
                ))}
              </SelectField>
            </Form.Item>
            {/* GTN No – Only for Pharmacy / Drug Store & Non-service/asset */}
            {!isNonStockItem &&
              (authInfo?.company_type === "Drug Store" ||
                authInfo?.company_type === "Pharmacy") && (
                <Form.Item
                  label="GTN No"
                  name="gtn_num"
                  rules={[
                    {
                      required: true,
                      message: "GTN Number is Required",
                    },
                  ]}
                >
                  <InputNumberField
                    min={0}
                    precision={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}

            <Form.Item name="manufacture_id" hidden>
              <input type="hidden" />
            </Form.Item>
            <Form.Item name="manufacture_name" hidden>
              <input type="hidden" />
            </Form.Item>
            <Form.Item label="Manufacture Name">
              <SearchListAutoComplete
                entityType="manufacture"
                placeholder="Manufacture"
                optionsList={(manufactures || []).map((m) => ({
                  sale_id: m.id,
                  display_text: m.name,
                  raw: m,
                }))}
                showAddNew={true}
                value={
                  watchedManufactureName
                    ? { display_text: watchedManufactureName }
                    : null
                }
                onSelect={handleManufactureSelect} // 👈 capture Add New here
                data-testid="manufacture-autocomplete"
              />
            </Form.Item>
            <Form.Item name="distributer_id" hidden>
              <input type="hidden" />
            </Form.Item>
            <Form.Item name="distributer_name" hidden>
              <input type="hidden" />
            </Form.Item>
            <Form.Item label="Distributor Name">
              <SearchListAutoComplete
                entityType="Distributor"
                placeholder="Distributor"
                optionsList={(distributors || []).map((m) => ({
                  sale_id: m.id,
                  display_text: m.name,
                  raw: m,
                }))}
                showAddNew={true}
                value={
                  watchedDistributerName
                    ? { display_text: watchedDistributerName }
                    : null
                }
                onSelect={handleDistributorSelect} // 👈 capture Add New here
                data-testid="distributor-autocomplete"
              />
            </Form.Item>

            {!isEditItem && crossBranchSyncEnabled && (
              <>
                <Form.Item name="branch_list" hidden>
                  <input type="hidden" />
                </Form.Item>
                <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                  This item will be created in all {branches?.length || 0} branches — Cross-Branch Sync is enabled.
                </Text>
              </>
            )}
            {!isEditItem && !crossBranchSyncEnabled && (
              <Form.Item
                label="Choose Branch"
                name="branch_list"
                rules={[{ required: true, message: "Choose a Branch" }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Choose Branch"
                  data-testid="branch-select"
                  maxTagCount="responsive"
                  onChange={handleBranchChange}
                  maxTagPlaceholder={(omittedValues) => (
                    <span>+{omittedValues.length}</span>
                  )}
                >
                  <Option value="all">All</Option>

                  {branches?.map((b) => (
                    <Option key={b.id} value={b.id}>
                      {b.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </Flex>
          <Flex
            vertical
            gap={10}
            style={{
              width: !screens.md || !screens.xl ? "100%" : "40%",
            }}
          >
            <Form.Item
              label="Purchase Rate"
              name="purchase_rate"
              // rules={[{ required: true, message: "Purchase Rate is Required" }]}
              style={{ display: "none" }}
            >
              <InputNumberField min={0} style={{ width: "100%" }} />
            </Form.Item>
            {/* Retail Rate / MRP — Pharmacy (all countries) + Drug Store UAE */}
            {showTax && (
              <Form.Item
                label={isIndia ? "Product GST %" : "Product VAT %"}
                name="tax_per"
                help={isGSTDisabled ? "Product GST is disabled for your account" : ""}
                rules={
                  isGSTDisabled
                    ? []
                    : [
                      {
                        required: true,
                        message: isIndia
                          ? "Product GST % is Required"
                          : "Product VAT % is Required",
                      },
                    ]
                }
              >
                {isIndia ? (
                  <SelectField
                    placeholder="Select GST %"
                    disabled={isGSTDisabled}
                  >
                    <Option value={0}>GST 0%</Option>
                    <Option value={5}>GST 5%</Option>
                    <Option value={18}>GST 18%</Option>
                    <Option value={40}>GST 40%</Option>
                  </SelectField>
                ) : (
                  <InputNumberField
                    min={0}
                    max={100}
                    style={{ width: "100%" }}
                    disabled={isGSTDisabled}
                  />
                )}
              </Form.Item>
            )}
            {showTax && !isGSTDisabled && (
              <div style={{ marginBottom: priceInclGst ? 0 : -4 }}>
                <Checkbox
                  checked={priceInclGst}
                  onChange={(e) => {
                    setPriceInclGst(e.target.checked);
                    if (!e.target.checked) setInclusivePrice(null);
                  }}
                >
                  {`Enter price including ${taxLabel}`}
                </Checkbox>
              </div>
            )}
            {showTax && !isGSTDisabled && priceInclGst && (
              <Form.Item
                label={`Price incl. ${taxLabel}${taxMissing ? "" : ` (${watchedTaxPer}%)`}`}
                validateStatus={taxMissing ? "warning" : undefined}
                help={
                  taxMissing
                    ? `Select Product ${taxLabel} % below to calculate the Retail Rate`
                    : undefined
                }
              >
                <InputNumberField
                  min={0}
                  style={{ width: "100%" }}
                  value={inclusivePrice}
                  onChange={(v) => setInclusivePrice(v)}
                />
              </Form.Item>
            )}
            <Form.Item
              label={`${isDrugStore || isPharmacy ? "Retail Rate / MRP" : "Retail Rate"
                }${priceInclGst ? ` (excl. ${taxLabel}, auto-calculated)` : ""}`}
              name="sale_rate"
              rules={[
                {
                  required: true,
                  message:
                    isDrugStore || isPharmacy
                      ? "Retail Rate / MRP is Required"
                      : "Retail Rate is Required",
                },
              ]}
            >
              <InputNumberField
                min={0}
                style={{ width: "100%" }}
                precision={priceInclGst ? 4 : 2}
                disabled={priceInclGst}
              />
            </Form.Item>
            {/* Discount % — Pharmacy (all countries) + Drug Store UAE only */}
            {(isDrugStore || isPharmacy) && (
              <Form.Item
                label="Discount %"
                name="discount_per"
                initialValue={0}
              >
                <InputNumberField
                  min={0}
                  max={100}
                  style={{ width: "100%" }}
                  suffix="%"
                />
              </Form.Item>
            )}
            {/* MRP — hide for Pharmacy and Drug Store UAE */}
            {!isDrugStore && !isPharmacy && (
              <Form.Item label="MRP" name="mrp_rate">
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            )}

            {/* Wholesaler → Class A / Net Selling Rate (Drug Store UAE only) */}
            {isDrugStore ? (
              <Form.Item label="Class A / Net Selling Rate" name="wholesaler_rate">
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            ) : !isPharmacy ? (
              <Form.Item label="Wholesaler Rate" name="wholesaler_rate">
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            ) : null}

            {/* Corporate → Class B Rate (Drug Store UAE only) */}
            {isDrugStore ? (
              <Form.Item label="Class B Rate" name="corporate_rate">
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            ) : !isPharmacy ? (
              <Form.Item label="Corporate Rate" name="corporate_rate">
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            ) : null}

            {/* Distributor → Class C Rate (Drug Store UAE only) */}
            {isDrugStore ? (
              <Form.Item label="Class C Rate" name="distributor_rate">
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            ) : !isPharmacy ? (
              <Form.Item label="Distributor Rate" name="distributor_rate">
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            ) : null}

            {/* Opening Stock Rate — renamed for Pharmacy + Drug Store UAE; disabled for services/assets; superseded by the per-branch table below when 2+ branches are selected */}
            {watchedBranchList.length <= 1 && (
              <Form.Item
                label={(isDrugStore || isPharmacy)
                  ? "Opening Stock Net Cost Per Quantity"
                  : "Opening Stock Rate Per Quantity"}
                name="opening_rate"
                initialValue={0}
                rules={[{ required: !isNonStockItem, message: "Opening Stock Rate is Required" }]}
              >
                <InputNumberField min={0} style={{ width: "100%" }} disabled={isNonStockItem} onClick={() => {
                  if (form.getFieldValue("opening_rate") === 0) {
                    form.setFieldValue("opening_rate", "");
                  }
                }} />
              </Form.Item>
            )}

            {isDrugStore && (
              <Form.Item
                label="MOH Invoice Rate"
                name="moh_invoice_rate"
              >
                <InputNumberField min={0} style={{ width: "100%" }} />
              </Form.Item>
            )}
            {watchedBranchList.length <= 1 && (
              <Form.Item
                label="Opening Stock Quantity"
                name="opening_qty"
                initialValue={0}
                rules={[
                  {
                    required: !isNonStockItem,
                    message: "Opening Stock Quantity is Required",
                  },
                ]}
              >
                <InputNumberField
                  min={0}
                  precision={0}
                  style={{ width: "100%" }}
                  disabled={isNonStockItem}
                  onClick={() => {
                    if (form.getFieldValue("opening_qty") === 0) {
                      form.setFieldValue("opening_qty", "");
                    }
                  }}
                />
              </Form.Item>
            )}

            {/* Per-branch Opening Stock Qty/Rate — shown instead of the single fields above when 2+ branches are selected */}
            {watchedBranchList.length >= 2 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ marginBottom: 8 }}>
                  Opening Stock (per branch){" "}
                  {!isNonStockItem && <span style={{ color: "#ff4d4f" }}>*</span>}
                </div>
                {watchedBranchList.map((branchId) => {
                  const branchName =
                    branches?.find((b) => b.id === branchId)?.name || branchId;
                  const entry = branchOpeningData[branchId] || {
                    opening_qty: 0,
                    opening_rate: 0,
                  };
                  return (
                    <Flex key={branchId} gap={12} align="center" style={{ marginBottom: 8 }}>
                      <span style={{ width: 160 }}>{branchName}</span>
                      <InputNumberField
                        min={0}
                        precision={0}
                        placeholder="Opening Qty"
                        style={{ width: "100%" }}
                        disabled={isNonStockItem}
                        value={entry.opening_qty}
                        onChange={(v) =>
                          setBranchOpeningData((prev) => ({
                            ...prev,
                            [branchId]: { ...prev[branchId], opening_qty: v ?? 0 },
                          }))
                        }
                      />
                      <InputNumberField
                        min={0}
                        precision={2}
                        placeholder={
                          (isDrugStore || isPharmacy)
                            ? "Opening Net Cost"
                            : "Opening Rate"
                        }
                        style={{ width: "100%" }}
                        disabled={isNonStockItem}
                        value={entry.opening_rate}
                        onChange={(v) =>
                          setBranchOpeningData((prev) => ({
                            ...prev,
                            [branchId]: { ...prev[branchId], opening_rate: v ?? 0 },
                          }))
                        }
                      />
                    </Flex>
                  );
                })}
              </div>
            )}

            <Form.Item label="Return Policy" name="return_days">
              <InputNumberField min={0} style={{ width: "100%" }} />
            </Form.Item>


            {/* <Form.Item
              label={isIndia ? "Product GST %" : "Product VAT %"}
              name="tax_per"
              help={isGSTDisabled ? "Product GST is disabled for your account" : ""}
              rules={
                isGSTDisabled
                  ? [] // ❌ no validation if disabled
                  : [
                    {
                      required: true,
                      message: isIndia
                        ? "Product GST % is Required"
                        : "Product VAT % is Required",
                    },
                  ]
              }
            >
              <InputNumberField
                min={0}
                max={100}
                style={{ width: "100%" }}
                disabled={isGSTDisabled}
              />
            </Form.Item> */}

            {/* {showTax && (
              <Form.Item
                label={isIndia ? "Product GST %" : "Product VAT %"}
                name="tax_per"
                help={isGSTDisabled ? "Product GST is disabled for your account" : ""}
                rules={
                  isGSTDisabled
                    ? []
                    : [
                      {
                        required: true,
                        message: isIndia
                          ? "Product GST % is Required"
                          : "Product VAT % is Required",
                      },
                    ]
                }
              >
                {isIndia ? (
                  <SelectField
                    placeholder="Select GST %"
                    disabled={isGSTDisabled}
                  >
                    <Option value={0}>GST 0%</Option>
                    <Option value={5}>GST 5%</Option>
                    <Option value={18}>GST 18%</Option>
                    <Option value={40}>GST 40%</Option>
                  </SelectField>
                ) : (
                  <InputNumberField
                    min={0}
                    max={100}
                    style={{ width: "100%" }}
                    disabled={isGSTDisabled}
                  />
                )}
              </Form.Item>
            )} */}

            {isGSTEnabled && (
              <Form.Item
                label="HSN/SAC Code"
                name="hsn_sac_code"
                rules={[
                  {
                    required: true,
                    message: "HSN/SAC Code is Required"
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      const str = String(value).trim();

                      if (!/^[0-9]{4,8}$/.test(str)) {
                        return Promise.reject(
                          "Enter valid HSN/SAC Code (4–8 digits)"
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputField placeholder="Enter HSN/SAC Code" />
              </Form.Item>)}

            <Form.Item label="Narration / Description" name="narration">
              <TextAreaField rows={3} />
            </Form.Item>

            <Form.Item label="Product Photo">
              <Upload
                beforeUpload={(file) => {
                  const isImage = file.type.startsWith("image/");
                  if (!isImage) {
                    message.error("Only image files are allowed!");
                    return Upload.LIST_IGNORE;
                  }
                  const isLt2M = file.size / 1024 / 1024 < 2;
                  if (!isLt2M) {
                    message.error("Image must be smaller than 2MB!");
                    return Upload.LIST_IGNORE;
                  }

                  if (file.name.length > 35) {
                    Swal.fire({
                      icon: "warning",
                      title: "Image file name is too long",
                      text: "Please rename the file to a shorter name and try again.",
                    });
                    return Upload.LIST_IGNORE;
                  }


                  return false; // prevent auto upload
                }}
                showUploadList={false}
                onChange={onPhotoChange}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />}>Upload Photo</Button>
              </Upload>

              {preview && (
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    width: 100,
                    marginTop: 8,
                    marginLeft: 8,
                    borderRadius: 4,
                  }}
                />
              )}
            </Form.Item>
          </Flex>
        </Flex>
        <Form.Item
          style={{
            marginTop: "24px",
          }}
        >
          <Flex style={{ marginBottom: "32px", width: "25%" }} gap={32}>
            <Button
              size="large"
              shape="default"
              onClick={handleCancel}
              className="traic-button"
            >
              Back
            </Button>
            {access_type == "WRITE" && (
              <>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  className="traic-button"
                  data-testid="save-product-btn"
                >
                  {isEditItem ? "Update" : "Save"}
                </Button>
              </>
            )}
          </Flex>
        </Form.Item>
      </Form>
      <Modal
        open={openManufactureModal}
        title="Add Manufacture"
        onCancel={() => setOpenManufactureModal(false)}
        footer={null}
      >
        <InputField
          rows={6}
          value={manufactureName}
          onChange={(e) => setManufactureName(e.target.value)}
          placeholder="Enter Manufacture Name..."
          data-testid="manufacture-name-input"
        />
        <InputNumber
          min={6}
          max={12}
          precision={0}
          value={manufactureGcpLength}
          onChange={(value) => setManufactureGcpLength(value)}
          placeholder="Company Prefix Length (6-12, optional)"
          style={{ width: "100%", marginTop: "10px" }}
          data-testid="manufacture-gcp-length-input"
        />
        <Button
          type="primary"
          loading={saving}
          onClick={handleSaveManufacture}
          block
          style={{ marginTop: "10px" }}
          data-testid="manufacture-save-btn"
        >
          Save
        </Button>
      </Modal>
      <Modal
        open={openDistributorModal}
        title="Add Distributor"
        onCancel={() => setOpenDistributorModal(false)}
        footer={null}
      >
        <InputField
          rows={6}
          value={distributorName}
          onChange={(e) => setDistributorName(e.target.value)}
          placeholder="Enter Distributor Name..."
          data-testid="distributor-name-input"
        />
        <Button
          type="primary"
          loading={saving}
          onClick={handleSaveDistributor}
          block
          style={{ marginTop: "10px" }}
          data-testid="distributor-save-btn"
        >
          Save
        </Button>
      </Modal>

      <AddMasterModal
        open={openMasterModal}
        onClose={() => setOpenMasterModal(false)}
        onSave={handleSaveMaster}
        title={
          masterType === "model" && (isDrugStore || isPharmacy)
            ? "Add New Sub Category"
            : `Add New ${masterType?.toUpperCase()}`
        }
        masterType={masterType}
        groups={groups}
        categories={categories}
        defaultGroupId={selectedGroupId}
        defaultCategoryId={selectedCategoryId}
      />
    </div>
  );
};

export default CreateItemForm;
